import React, { useEffect, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import {
  DispatchNote,
  DispatchCustomer,
  TraceProducedLot,
  DispatchNoteLinePayload,
} from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";

type DraftLine = DispatchNoteLinePayload & { _key: number };

const formatDate = (dateStr?: string | null) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("hr-HR");
};

const DispatchNotes = () => {
  savePathToLocalStorage();

  const [notes, setNotes] = useState<DispatchNote[]>([]);
  const [customers, setCustomers] = useState<DispatchCustomer[]>([]);
  const [producedLots, setProducedLots] = useState<TraceProducedLot[]>([]);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const [customerId, setCustomerId] = useState<number>(0);
  const [dispatchDate, setDispatchDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [noteText, setNoteText] = useState<string>("");
  const [lines, setLines] = useState<DraftLine[]>([]);

  useEffect(() => {
    loadNotes();
  }, []);

  const loadNotes = async () => {
    try {
      setNotes(await mesApi.getDispatchNotes());
    } catch (error: any) {
      toast.error(`Greška kod učitavanja otpremnica: ${error.message}`);
    }
  };

  const startCreate = async () => {
    setCreating(true);
    setCustomerId(0);
    setDispatchDate(new Date().toISOString().split("T")[0]);
    setNoteText("");
    setLines([{ _key: Date.now() }]);
    try {
      const [cust, lots] = await Promise.all([
        mesApi.getDispatchCustomers(),
        mesApi.traceProducedLots(),
      ]);
      setCustomers(cust);
      setProducedLots(lots);
    } catch (error: any) {
      toast.error(`Greška: ${error.message}`);
    }
  };

  const addLine = () => setLines((prev) => [...prev, { _key: Date.now() + Math.random() }]);
  const removeLine = (key: number) => setLines((prev) => prev.filter((l) => l._key !== key));
  const updateLine = (key: number, patch: Partial<DraftLine>) =>
    setLines((prev) => prev.map((l) => (l._key === key ? { ...l, ...patch } : l)));

  const handleSave = async () => {
    const validLines = lines.filter((l) => l.batch_id);
    if (!customerId) {
      toast.warn("Odaberi kupca");
      return;
    }
    if (validLines.length === 0) {
      toast.warn("Dodaj barem jedan proizvedeni lot");
      return;
    }
    try {
      setSaving(true);
      await mesApi.createDispatchNote({
        customer_id: customerId,
        dispatch_date: dispatchDate,
        notes: noteText || undefined,
        lines: validLines.map((l) => ({
          batch_id: l.batch_id,
          quantity: l.quantity ? Number(l.quantity) : undefined,
        })),
      });
      toast.success("Otpremnica kreirana");
      setCreating(false);
      loadNotes();
    } catch (error: any) {
      toast.error(`Greška kod spremanja: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mes-page">
      <Header title="MES - Skladiste" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          <ToastContainer position="top-center" autoClose={2000} />

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h2>Otpremnice</h2>
            {!creating && (
              <button className="btn-primary" onClick={startCreate}>+ Nova otpremnica</button>
            )}
          </div>

          {creating ? (
            <div className="mes-section">
              <h3 style={{ marginBottom: "15px" }}>Nova otpremnica</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 200px", gap: "15px", marginBottom: "15px" }}>
                <div>
                  <label style={{ display: "block", marginBottom: "5px", color: "#666" }}>Kupac</label>
                  <select
                    value={customerId}
                    onChange={(e) => setCustomerId(parseInt(e.target.value))}
                    style={{ width: "100%", padding: "8px", border: "1px solid #ddd", borderRadius: "4px" }}
                  >
                    <option value={0}>-- Odaberi kupca --</option>
                    {customers.map((c) => (
                      <option key={c.ID} value={c.ID}>{c.Name || `#${c.ID}`}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: "5px", color: "#666" }}>Datum</label>
                  <input
                    type="date"
                    value={dispatchDate}
                    onChange={(e) => setDispatchDate(e.target.value)}
                    style={{ width: "100%", padding: "8px", border: "1px solid #ddd", borderRadius: "4px" }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px", color: "#666" }}>Napomena</label>
                <input
                  type="text"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  style={{ width: "100%", padding: "8px", border: "1px solid #ddd", borderRadius: "4px" }}
                />
              </div>

              <h4 style={{ marginBottom: "10px" }}>Stavke (proizvedeni lotovi)</h4>
              <div className="mes-table-wrap">
                <table className="mes-table" style={{ marginBottom: "10px" }}>
                <thead>
                  <tr>
                    <th>Proizvedeni lot</th>
                    <th style={{ width: "140px", textAlign: "right" }}>Količina</th>
                    <th style={{ width: "40px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={l._key}>
                      <td>
                        <select
                          value={l.batch_id || 0}
                          onChange={(e) => updateLine(l._key, { batch_id: parseInt(e.target.value) || undefined })}
                          style={{ width: "100%", padding: "6px", border: "1px solid #ddd", borderRadius: "4px" }}
                        >
                          <option value={0}>-- Odaberi lot --</option>
                          {producedLots.map((b) => (
                            <option key={b.batch_id} value={b.batch_id}>
                              {b.production_lot_code || b.mix_code} · {b.product_name || "-"}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.001"
                          value={l.quantity ?? ""}
                          onChange={(e) => updateLine(l._key, { quantity: e.target.value ? Number(e.target.value) : undefined })}
                          style={{ width: "100%", padding: "6px", border: "1px solid #ddd", borderRadius: "4px", textAlign: "right" }}
                        />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button className="btn-secondary btn-sm" onClick={() => removeLine(l._key)} disabled={lines.length === 1}>
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              <button className="btn-secondary btn-sm" onClick={addLine} style={{ marginBottom: "20px" }}>
                + Dodaj stavku
              </button>

              <div style={{ display: "flex", gap: "10px" }}>
                <button className="btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? "Spremanje..." : "Spremi otpremnicu"}
                </button>
                <button className="btn-secondary" onClick={() => setCreating(false)} disabled={saving}>
                  Odustani
                </button>
              </div>
            </div>
          ) : (
            <div className="mes-section">
              {notes.length === 0 ? (
                <p style={{ textAlign: "center", padding: "40px", color: "#999", fontStyle: "italic" }}>
                  Još nema otpremnica
                </p>
              ) : (
                <div className="mes-table-wrap">
                  <table className="mes-table">
                  <thead>
                    <tr>
                      <th>Broj</th>
                      <th>Datum</th>
                      <th>Kupac</th>
                      <th style={{ textAlign: "right" }}>Stavki</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notes.map((n) => (
                      <tr key={n.ID}>
                        <td><strong>{n.DispatchNumber || `#${n.ID}`}</strong></td>
                        <td>{formatDate(n.DispatchDate)}</td>
                        <td>{n.Company_DispatchNoteToCompany?.Name || "-"}</td>
                        <td style={{ textAlign: "right" }}>{n.DispatchNoteLine?.length ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DispatchNotes;
